/**
 * WhatsAppTemplateFlow — M2.4 · CRM Template Messaging (state machine UI).
 *
 * Orchestratore UNICO. Tutto avviene DENTRO il modal. La pagina CRM
 * NON si trasforma mai in selection mode (M2.5 hotfix P0-B).
 *
 * Phases: idle → PICKER → RECIPIENTS → REVIEW → PROGRESS · HISTORY parallela.
 *
 * Produzione validation gate:
 *   - Se `provider_verified === false` il tasto "Continua" del picker è
 *     disabilitato con messaggio esplicito.
 *   - Se il backend blocca il batch (status=blocked, verify live fallita),
 *     mostriamo l'errore chiaramente all'operatore.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Send, RefreshCcw, X, AlertTriangle, CheckCircle2, Clock,
  MessageCircleWarning, ChevronRight, Stethoscope, Search, Users, Phone,
  Layers, Plus, Minus,
} from "lucide-react";
import { ListPickerModal } from "@/components/CustomerListsChips";
import { Upload, RefreshCw, Trash2, ExternalLink, FileText, File as FileIcon, X as XIcon, CheckCircle2 as CheckIcon, Loader2, AlertCircle, RotateCcw, Video as VideoIcon, Image as ImageIcon } from "lucide-react";


// ---------------------------------------------------------------------------
// Upload progress helpers (usati solo dalla UX; nessuna influenza su logica
// upload backend, invio Meta, storage o DB).
// ---------------------------------------------------------------------------
function _fmtSpeed(bytesPerSec) {
  if (!bytesPerSec || !Number.isFinite(bytesPerSec)) return "—";
  const mbs = bytesPerSec / 1024 / 1024;
  if (mbs >= 1) return `${mbs.toFixed(1)} MB/s`;
  const kbs = bytesPerSec / 1024;
  return `${kbs.toFixed(0)} KB/s`;
}
function _fmtEta(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "—";
  if (sec < 60) return `${Math.max(1, Math.round(sec))} s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  return `${m} min ${s} s`;
}
function _phaseLabel(phase) {
  return ({
    preparing: "Preparazione file…",
    uploading: "Upload in corso…",
    processing: "Elaborazione…",
    done: "Upload completato",
    error: "Errore upload",
  })[phase] || "…";
}


function UploadProgressBar({ state }) {
  if (!state) return null;
  const { phase, filename, loaded = 0, total = 0, speedBps = 0, etaSec = null, error } = state;
  const pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100))
              : (phase === "processing" || phase === "done" ? 100 : 0);
  const isError = phase === "error";
  const isDone = phase === "done";
  const isIndeterminate = phase === "preparing" || (phase === "processing" && pct >= 100);

  return (
    <div data-testid="tpl-media-upload-progress"
          className={`rounded-lg border p-3 ${
            isError ? "border-rose-300 bg-rose-50" :
            isDone ? "border-emerald-300 bg-emerald-50" :
            "border-sky-300 bg-sky-50"}`}>
      <div className="flex items-center gap-2 text-xs font-semibold">
        {isError ? <AlertCircle className="w-4 h-4 text-rose-700" /> :
         isDone ? <CheckIcon className="w-4 h-4 text-emerald-700" /> :
         <Loader2 className="w-4 h-4 animate-spin text-sky-700" />}
        <span data-testid="tpl-media-upload-phase"
               className={isError ? "text-rose-900" : isDone ? "text-emerald-900" : "text-sky-900"}>
          {_phaseLabel(phase)}
        </span>
        {filename && (
          <span className="text-ink-500 font-normal truncate ml-1">· {filename}</span>
        )}
        {!isError && !isIndeterminate && (
          <span data-testid="tpl-media-upload-percent"
                 className="ml-auto tabular-nums font-mono text-sky-900">
            {pct}%
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-2 h-2 rounded-full bg-sand-200 overflow-hidden">
        <div className={`h-full transition-all duration-200 ease-out ${
                isError ? "bg-rose-500" :
                isDone ? "bg-emerald-500" :
                "bg-bronze-600"} ${isIndeterminate ? "animate-pulse w-1/3" : ""}`}
              style={!isIndeterminate ? { width: `${pct}%` } : undefined}
              data-testid="tpl-media-upload-bar" />
      </div>

      {/* Dettagli numerici (solo durante upload effettivo) */}
      {!isError && total > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-ink-700 tabular-nums">
          <span data-testid="tpl-media-upload-bytes">
            {_fmtSize(loaded)} / {_fmtSize(total)}
          </span>
          {phase === "uploading" && (
            <>
              <span data-testid="tpl-media-upload-speed">{_fmtSpeed(speedBps)}</span>
              <span data-testid="tpl-media-upload-eta">Tempo rimanente: {_fmtEta(etaSec)}</span>
            </>
          )}
        </div>
      )}

      {isError && error && (
        <div className="mt-2 text-[11px] text-rose-900 break-words">
          <b>Motivo:</b> {error}
        </div>
      )}
    </div>
  );
}


// ===========================================================================
// TemplateMediaInline — pannello media inline (solo per HEADER media).
// Fonte di verità: whatsapp_template_media (backend). NESSUNO stato media
// duplicato nel frontend oltre alla view corrente.
// Miglioria (2026-02-27): preview inline del media caricato:
//   - IMAGE  → <img> clickabile con lightbox
//   - VIDEO  → player HTML5 con poster, no autoplay
//   - DOCUMENT → icona + filename + size + pulsante "Apri" (nuova tab)
// Il binario è servito via `/media/binary` (auth-gated, streaming dal
// Storage Layer). La blob URL è revocata su unmount / cambio record.
// ===========================================================================
function _fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function _statusLabel({ uploading, refreshing, record, expired }) {
  if (uploading) return { label: "Upload in corso", color: "text-sky-800", bg: "bg-sky-100", Icon: Loader2, spin: true };
  if (refreshing || record?.meta_refresh_status === "refreshing") {
    return { label: "In refresh", color: "text-sky-800", bg: "bg-sky-100", Icon: Loader2, spin: true };
  }
  if (record?.storage_status === "missing_original") {
    return { label: "File originale mancante", color: "text-rose-800", bg: "bg-rose-100", Icon: AlertCircle };
  }
  if (record?.meta_refresh_status === "failed") {
    return { label: "Errore", color: "text-rose-800", bg: "bg-rose-100", Icon: AlertCircle };
  }
  if (expired) return { label: "Scaduto — refresh automatico", color: "text-amber-800", bg: "bg-amber-100", Icon: AlertCircle };
  if (record?.exists && record?.meta_media_id) {
    return { label: "Pronto", color: "text-emerald-800", bg: "bg-emerald-100", Icon: CheckIcon };
  }
  return { label: "Non caricato", color: "text-rose-800", bg: "bg-rose-100", Icon: AlertCircle };
}


function MediaPreview({ format, blobUrl, filename, mime, onEnlarge }) {
  if (!blobUrl) {
    return (
      <div className="flex items-center justify-center rounded-lg bg-sand-100 border border-sand-200 h-32 text-xs text-ink-400"
           data-testid="tpl-media-preview-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-1" /> Caricamento anteprima…
      </div>
    );
  }
  if (format === "IMAGE") {
    return (
      <button type="button" onClick={onEnlarge}
              data-testid="tpl-media-preview-image"
              className="block w-full rounded-lg overflow-hidden border border-sand-200 bg-sand-50 hover:opacity-90 transition">
        <img src={blobUrl} alt={filename}
              className="w-full h-auto max-h-64 object-contain cursor-zoom-in" />
      </button>
    );
  }
  if (format === "VIDEO") {
    return (
      <video src={blobUrl} controls preload="metadata"
              data-testid="tpl-media-preview-video"
              className="block w-full max-h-64 rounded-lg border border-sand-200 bg-black" />
    );
  }
  // DOCUMENT
  const isPdf = (mime || "").includes("pdf");
  return (
    <div data-testid="tpl-media-preview-document"
          className="flex items-center gap-3 rounded-lg border border-sand-200 bg-white p-3">
      <div className="rounded-lg bg-sand-100 p-2">
        {isPdf ? <FileText className="w-8 h-8 text-rose-700" /> : <FileIcon className="w-8 h-8 text-ink-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{filename}</div>
        <div className="text-[11px] text-ink-500">{mime || "documento"}</div>
      </div>
      <a href={blobUrl} target="_blank" rel="noopener noreferrer"
          data-testid="tpl-media-preview-document-open"
          className="inline-flex items-center gap-1 rounded-full border border-sky-500 bg-white text-sky-800 px-2.5 py-1 text-xs">
        <ExternalLink className="w-3 h-3" /> Apri
      </a>
    </div>
  );
}


function ImageLightbox({ src, filename, onClose }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div data-testid="tpl-media-lightbox"
          onClick={onClose}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6">
      <button onClick={onClose}
               data-testid="tpl-media-lightbox-close"
               className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 text-white p-2">
        <XIcon className="w-5 h-5" />
      </button>
      <img src={src} alt={filename}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
    </div>
  );
}


function TemplateMediaInline({ template, onReadyChange, onRecordChange, variant = "compact" }) {
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [record, setRecord] = React.useState(null);
  const [blobUrl, setBlobUrl] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  // Progress upload · unico source-of-truth per stato/pct/velocità/ETA.
  const [uploadState, setUploadState] = React.useState(null);
  // File da ritentare (mantenuto solo se ultimo upload ha fallito).
  const [pendingRetryFile, setPendingRetryFile] = React.useState(null);
  const fileRef = React.useRef(null);
  const blobUrlRef = React.useRef(null);
  // Anti-doppio-click / anti-upload-concorrenti (immediato, senza attendere
  // il re-render di `uploadState`).
  const inFlightRef = React.useRef(false);
  // Sample per calcolo velocità (media mobile sugli ultimi ~2s).
  const progressSamplesRef = React.useRef([]);
  const uploading = !!uploadState && ["preparing", "uploading", "processing"].includes(uploadState.phase);

  const revokeBlob = React.useCallback(() => {
    if (blobUrlRef.current) {
      try { URL.revokeObjectURL(blobUrlRef.current); } catch { /* silent */ }
      blobUrlRef.current = null;
    }
    setBlobUrl(null);
  }, []);

  const loadBinary = React.useCallback(async (rec) => {
    revokeBlob();
    if (!rec?.exists || !rec?.meta_media_id) return;
    setPreviewLoading(true);
    try {
      const url = `/admin/messaging/templates/${encodeURIComponent(template.name)}/media/binary?language=${encodeURIComponent(template.language)}`;
      const res = await api.get(url, { responseType: "blob" });
      const objUrl = URL.createObjectURL(res.data);
      blobUrlRef.current = objUrl;
      setBlobUrl(objUrl);
    } catch (e) {
      // silente: la mancata preview non blocca il flow
    } finally { setPreviewLoading(false); }
  }, [template.name, template.language, revokeBlob]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const url = `/admin/messaging/templates/${encodeURIComponent(template.name)}/media?language=${encodeURIComponent(template.language)}`;
      const { data } = await api.get(url);
      setRecord(data);
      onReadyChange?.(!!(data?.exists && data?.meta_media_id));
      onRecordChange?.(data);
      await loadBinary(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [template.name, template.language, onReadyChange, onRecordChange, loadBinary]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => () => revokeBlob(), [revokeBlob]);   // unmount cleanup

  const doUpload = React.useCallback(async (file) => {
    if (!file) return;
    if (inFlightRef.current) return;   // guard hard: nessun upload concorrente.
    inFlightRef.current = true;
    progressSamplesRef.current = [];
    setPendingRetryFile(null);
    setUploadState({
      phase: "preparing", filename: file.name,
      loaded: 0, total: file.size || 0, speedBps: 0, etaSec: null, error: null,
    });

    try {
      const form = new FormData();
      form.append("file", file);
      const url = `/admin/messaging/templates/${encodeURIComponent(template.name)}/media?language=${encodeURIComponent(template.language)}`;
      const startMs = Date.now();

      const { data } = await api.post(url, form, {
        headers: { "Content-Type": "multipart/form-data" },
        // NB: onUploadProgress usa la callback nativa di XHR/axios in
        // browser. NESSUN cambio alla logica di upload backend/Meta:
        // stiamo solo ascoltando l'avanzamento del transfer già in atto.
        onUploadProgress: (ev) => {
          const total = ev.total || file.size || 0;
          const loaded = ev.loaded || 0;
          // Sliding window ~2s per stabilizzare la velocità.
          const now = Date.now();
          const samples = progressSamplesRef.current;
          samples.push({ t: now, loaded });
          const cutoff = now - 2000;
          while (samples.length > 2 && samples[0].t < cutoff) samples.shift();
          const first = samples[0];
          const elapsedS = Math.max(0.001, (now - first.t) / 1000);
          const speedBps = (loaded - first.loaded) / elapsedS;
          const remaining = Math.max(0, total - loaded);
          const etaSec = speedBps > 0 ? remaining / speedBps : null;
          // Se il transfer è al 100%, la request è in mano al server
          // (processing lato backend: upload → Meta → persist DB).
          const phase = (total > 0 && loaded >= total) ? "processing" : "uploading";
          setUploadState((prev) => prev && ({
            ...prev, phase, loaded, total, speedBps, etaSec,
          }));
        },
      });

      const rec = { exists: true, ...data };
      setUploadState((prev) => prev && ({
        ...prev, phase: "done", loaded: rec.byte_size || prev.loaded,
        total: rec.byte_size || prev.total,
      }));
      setRecord(rec);
      onReadyChange?.(!!data?.meta_media_id);
      onRecordChange?.(rec);
      await loadBinary(rec);
      toast.success("Media caricato con successo");
      // Nasconde la progress bar dopo 1.2s per feedback "done" visibile.
      setTimeout(() => {
        setUploadState((prev) => (prev && prev.phase === "done" ? null : prev));
      }, 1200);
    } catch (e) {
      const msg = formatApiError(e);
      // Preserva il media precedente: NON tocchiamo `record`. Solo la
      // progress bar entra in fase error + salviamo il file per retry.
      setUploadState((prev) => (prev ? { ...prev, phase: "error", error: msg } : {
        phase: "error", filename: file.name,
        loaded: 0, total: file.size || 0, speedBps: 0, etaSec: null, error: msg,
      }));
      setPendingRetryFile(file);
      toast.error(msg);
    } finally {
      inFlightRef.current = false;
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [template.name, template.language, onReadyChange, loadBinary]);

  const handleUpload = async (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
    const file = ev.target.files?.[0];
    if (!file) return;
    await doUpload(file);
  };

  const handleRetry = (ev) => {
    ev.stopPropagation(); ev.preventDefault();
    if (pendingRetryFile) doUpload(pendingRetryFile);
  };

  const handleDismissError = (ev) => {
    ev.stopPropagation(); ev.preventDefault();
    setUploadState(null);
    setPendingRetryFile(null);
  };

  const handleRefresh = async (ev) => {
    ev.stopPropagation(); ev.preventDefault();
    setRefreshing(true);
    try {
      const url = `/admin/messaging/templates/${encodeURIComponent(template.name)}/media/refresh?language=${encodeURIComponent(template.language)}`;
      const { data } = await api.post(url);
      const rec = { exists: true, ...data };
      setRecord(rec);
      onRecordChange?.(rec);
      toast.success("media_id rinnovato");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRefreshing(false); }
  };

  const handleDelete = async (ev) => {
    ev.stopPropagation(); ev.preventDefault();
    if (!window.confirm("Rimuovere il media associato a questo template?")) return;
    try {
      const url = `/admin/messaging/templates/${encodeURIComponent(template.name)}/media?language=${encodeURIComponent(template.language)}`;
      await api.delete(url);
      revokeBlob();
      await load();
      onRecordChange?.(null);
      toast.success("Media rimosso");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const acceptByFormat = {
    VIDEO: "video/mp4,video/3gp,video/3gpp",
    IMAGE: "image/jpeg,image/png",
    DOCUMENT: "application/pdf",
  }[(template.header_format || "").toUpperCase()] || "";

  if (loading) {
    return <div data-testid="tpl-media-loading" className="mt-3 text-xs text-ink-500">Verifica media…</div>;
  }

  const exists = record?.exists && record?.meta_media_id;
  const expired = exists && record.meta_expires_at && new Date(record.meta_expires_at) <= new Date();
  const status = _statusLabel({ uploading, refreshing, record, expired });
  const fmt = (template.header_format || record?.header_format || "").toUpperCase();
  const isDoc = fmt === "DOCUMENT";

  return (
    <div data-testid={`tpl-media-inline-${template.name}`}
          onClick={(e) => e.stopPropagation()}
          className={`${variant === "full" ? "" : "mt-3"} rounded-xl border ${variant === "full" ? "p-5" : "p-3"} ${variant === "full" ? "text-sm" : "text-xs"} ${
            exists && !expired ? "border-emerald-300 bg-emerald-50" :
            expired ? "border-amber-300 bg-amber-50" :
            "border-rose-300 bg-rose-50"}`}>
      {!exists ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-rose-900">
              <b>Media richiesto</b> per questo template ({template.header_format}) — caricalo per abilitare l'invio.
            </div>
            <label className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 ${
                    uploading ? "bg-sand-400 text-white cursor-wait pointer-events-none opacity-70" :
                    "bg-bronze-600 hover:bg-bronze-700 text-white cursor-pointer"}`}>
              <Upload className="w-3 h-3" />
              {uploading ? "Caricamento…" : "Carica"}
              <input ref={fileRef} type="file" accept={acceptByFormat}
                      disabled={uploading} onChange={handleUpload}
                      data-testid={`tpl-media-upload-${template.name}`}
                      className="hidden" />
            </label>
          </div>
          {uploadState && (
            <UploadProgressBar state={uploadState} />
          )}
          {uploadState?.phase === "error" && (
            <div className="flex gap-1.5">
              <button onClick={handleRetry} disabled={!pendingRetryFile}
                       data-testid="tpl-media-upload-retry"
                       className="inline-flex items-center gap-1 rounded-full bg-bronze-600 hover:bg-bronze-700 text-white px-2.5 py-1 text-xs">
                <RotateCcw className="w-3 h-3" /> Riprova
              </button>
              <button onClick={handleDismissError}
                       data-testid="tpl-media-upload-dismiss"
                       className="inline-flex items-center gap-1 rounded-full border border-ink-300 bg-white text-ink-700 px-2.5 py-1 text-xs">
                <XIcon className="w-3 h-3" /> Chiudi
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Anteprima media */}
          <MediaPreview format={fmt} blobUrl={blobUrl}
                         filename={record.original_filename}
                         mime={record.mime_type}
                         onEnlarge={() => setLightboxOpen(true)} />

          {previewLoading && !blobUrl && (
            <div className="text-[11px] text-ink-500 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Caricamento anteprima…
            </div>
          )}

          {/* Metadata + stato */}
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-ink-700">
            <div><span className="text-ink-500">Nome file:</span> <b data-testid="tpl-media-meta-filename">{record.original_filename}</b></div>
            <div><span className="text-ink-500">Formato:</span> <b>{fmt}</b> · <code className="text-[10px]">{record.mime_type}</code></div>
            <div><span className="text-ink-500">Dimensione:</span> <b>{_fmtSize(record.byte_size)}</b></div>
            <div className="flex items-center gap-1">
              <span className="text-ink-500">Stato Meta:</span>
              <span data-testid="tpl-media-status"
                     className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${status.color} ${status.bg}`}>
                <status.Icon className={`w-3 h-3 ${status.spin ? "animate-spin" : ""}`} /> {status.label}
              </span>
            </div>
            <div><span className="text-ink-500">Data upload:</span> {record.meta_uploaded_at ? new Date(record.meta_uploaded_at).toLocaleString("it-IT") : "—"}</div>
            <div className={expired ? "text-amber-700 font-semibold" : ""}>
              <span className="text-ink-500">Scadenza media_id:</span> {record.meta_expires_at ? new Date(record.meta_expires_at).toLocaleString("it-IT") : "—"}
            </div>
            <div><span className="text-ink-500">Ultimo refresh:</span> {record.meta_uploaded_at ? new Date(record.meta_uploaded_at).toLocaleString("it-IT") : "—"}</div>
          </div>

          {record.storage_status === "missing_original" && (
            <div data-testid="tpl-media-missing-original"
                 className="rounded-lg bg-rose-100 border border-rose-300 p-2 text-[11px] text-rose-900">
              <b>File originale non disponibile sullo storage.</b> Il media Meta attuale resta utilizzabile
              fino alla scadenza, ma il rinnovo automatico fallirà: <b>ricarica il file</b> per ripristinarlo.
            </div>
          )}

          {record.meta_refresh_status === "failed" && record.meta_last_refresh_error && (
            <div className="rounded-lg bg-rose-100 border border-rose-300 p-2 text-[11px] text-rose-900">
              <b>Ultimo refresh fallito:</b> {record.meta_last_refresh_error}
            </div>
          )}

          {/* Progress bar upload (sostituzione media in corso) */}
          {uploadState && (
            <UploadProgressBar state={uploadState} />
          )}
          {uploadState?.phase === "error" && (
            <div className="flex gap-1.5">
              <button onClick={handleRetry} disabled={!pendingRetryFile}
                       data-testid="tpl-media-upload-retry"
                       className="inline-flex items-center gap-1 rounded-full bg-bronze-600 hover:bg-bronze-700 text-white px-2.5 py-1 text-xs">
                <RotateCcw className="w-3 h-3" /> Riprova
              </button>
              <button onClick={handleDismissError}
                       data-testid="tpl-media-upload-dismiss"
                       className="inline-flex items-center gap-1 rounded-full border border-ink-300 bg-white text-ink-700 px-2.5 py-1 text-xs">
                <XIcon className="w-3 h-3" /> Chiudi
              </button>
            </div>
          )}

          {/* Azioni · disabilitate durante l'upload per prevenire doppi click.
              La logica di upload/scheduler/invio Meta non è toccata:
              disabilitiamo solo la superficie UI. */}
          <div className="flex gap-1.5 flex-wrap pt-1 border-t border-emerald-200/60">
            <label className={`inline-flex items-center gap-1 rounded-full border border-emerald-500 bg-white text-emerald-800 px-2.5 py-1 ${
                    uploading ? "opacity-50 cursor-wait pointer-events-none" : "cursor-pointer"}`}
                    aria-disabled={uploading || undefined}>
              <Upload className="w-3 h-3" /> Sostituisci
              <input ref={fileRef} type="file" accept={acceptByFormat}
                      disabled={uploading} onChange={handleUpload}
                      data-testid={`tpl-media-replace-${template.name}`}
                      className="hidden" />
            </label>
            <button onClick={handleRefresh} disabled={refreshing || uploading}
                     data-testid={`tpl-media-refresh-${template.name}`}
                     className={`inline-flex items-center gap-1 rounded-full border border-sky-500 bg-white text-sky-800 px-2.5 py-1 ${
                       (refreshing || uploading) ? "opacity-50 cursor-not-allowed" : ""}`}>
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Rinnova
            </button>
            {isDoc && blobUrl && (
              <a href={blobUrl} target="_blank" rel="noopener noreferrer"
                  data-testid={`tpl-media-open-${template.name}`}
                  className="inline-flex items-center gap-1 rounded-full border border-sky-500 bg-white text-sky-800 px-2.5 py-1">
                <ExternalLink className="w-3 h-3" /> Apri file
              </a>
            )}
            <button onClick={handleDelete} disabled={uploading}
                     data-testid={`tpl-media-delete-${template.name}`}
                     className={`inline-flex items-center gap-1 rounded-full border border-rose-500 bg-white text-rose-800 px-2.5 py-1 ml-auto ${
                       uploading ? "opacity-50 cursor-not-allowed" : ""}`}>
              <Trash2 className="w-3 h-3" /> Elimina
            </button>
          </div>
        </div>
      )}

      {lightboxOpen && blobUrl && fmt === "IMAGE" && (
        <ImageLightbox src={blobUrl} filename={record?.original_filename}
                        onClose={() => setLightboxOpen(false)} />
      )}
    </div>
  );
}


const PHASE = {
  PICKER: "picker",
  RECIPIENTS: "recipients",
  MEDIA: "media",          // Solo se chosenTemplate.header_format ∈ VIDEO/IMAGE/DOCUMENT
  REVIEW: "review",
  PROGRESS: "progress",
};


// ===========================================================================
// SORGENTE UNICA DEI DESTINATARI FINALI · P1-BUG-FIX
// ===========================================================================
// Questa funzione pura è l'ORACOLO del wizard per "quanti destinatari
// riceveranno il messaggio". La regola operativa è:
//
//   finalRecipients =
//     ( contactsFromIncludedLists ∪ manuallySelectedContacts )
//     − contactsFromExcludedLists
//     − serverSideExclusions   (Staff, numero non valido, opt-out, …)
//
// Il calcolo esatto (dedup + exclude + validazione telefono) richiede la
// conoscenza dei MEMBRI delle liste, che vive solo lato backend. Perciò:
//
//   - Se `livePreview.totals.eligible` è disponibile → è AUTORITATIVA
//     (viene dallo stesso `preview_batch` usato al momento dell'invio).
//   - Altrimenti fallback a stima client-side:
//         manual (selectAllFiltered ? filteredEligibleCount : selectedIds.size)
//       + Σ includeLists.members_count
//     La stima può sovrastimare in caso di overlap manual↔lista, ma è
//     sufficiente per abilitare Continua. Il numero esatto si vede alla
//     Review, che consuma anch'essa `preview_batch`.
//
// Il flag `hasAnySource` risponde a "esiste almeno una sorgente di
// destinatari valida?" (manual OR selectAll OR includeLists). È l'unico
// gate che il bottone Continua deve valutare quando `count === 0`.
// ===========================================================================
function computeFinalRecipients({ selectedIds, selectAllFiltered, filteredEligibleCount,
                                    includeLists, livePreview }) {
  const manualCount = selectAllFiltered
    ? (filteredEligibleCount || 0)
    : (selectedIds?.size || 0);
  const listMemberSum = (includeLists || []).reduce(
    (s, l) => s + (Number(l.members_count) || 0), 0);
  const hasAnySource = manualCount > 0 || (includeLists?.length || 0) > 0;

  if (livePreview?.totals?.eligible != null) {
    return {
      count: Number(livePreview.totals.eligible) || 0,
      authoritative: true,
      hasAnySource,
      breakdown: {
        manual: manualCount,
        fromLists: listMemberSum,
        serverEligible: Number(livePreview.totals.eligible) || 0,
        serverSelected: Number(livePreview.totals.selected) || 0,
        serverExcluded: Number(livePreview.totals.excluded) || 0,
      },
    };
  }
  // Estimate mode
  return {
    count: manualCount + listMemberSum,
    authoritative: false,
    hasAnySource,
    breakdown: { manual: manualCount, fromLists: listMemberSum,
                  serverEligible: null, serverSelected: null, serverExcluded: null },
  };
}

const SEGMENT_LABEL = {
  "": "Tutti", nuovo: "Nuovo", attivo: "Attivo",
  dormiente: "Dormiente", vip: "VIP",
};
const SEGMENT_ORDER = ["", "nuovo", "attivo", "dormiente", "vip"];

// Soglia oltre la quale una selezione richiede conferma esplicita.
const LARGE_SELECTION_THRESHOLD = 50;


export function WhatsAppTemplateFlow({ isOpen, onClose }) {
  const [phase, setPhase] = useState(PHASE.PICKER);
  const [tab, setTab] = useState("send");   // "send" | "history"

  // Templates
  const [templates, setTemplates] = useState([]);
  const [providerVerified, setProviderVerified] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshInfo, setRefreshInfo] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [chosenTemplate, setChosenTemplate] = useState(null);

  // Recipients (persist across PICKER↔RECIPIENTS back-and-forth)
  const [rSearch, setRSearch] = useState("");
  const [rSegment, setRSegment] = useState("");
  const [rRows, setRRows] = useState([]);
  const [rLoading, setRLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [confirmLargeSelection, setConfirmLargeSelection] = useState(false);

  // P0-6 FASE 2G+ · Include / Exclude CRM Lists (audience server-side).
  // includeLists / excludeLists: array di {id, name, color, members_count}.
  // Staff pre-selezionata in exclude di default (se la lista esiste),
  // rimovibile manualmente per singolo batch. Non è hardcoded backend.
  const [includeLists, setIncludeLists] = useState([]);
  const [excludeLists, setExcludeLists] = useState([]);
  const [staffAutoApplied, setStaffAutoApplied] = useState(false);
  const [listPickerMode, setListPickerMode] = useState(null);  // "include" | "exclude" | null

  // P0-6 · Default exclusions deterministiche — al primo apertura del modal.
  // NON usa più q-search testuale ambigua (`?q=Staff` matcherebbe anche
  // "Staff Estate", "Ex Staff", "Staff Fornitori"). Chiama invece l'endpoint
  // server-authoritative che restituisce SOLO la lista di sistema identificata
  // via `name_normalized == "staff"` (unique index).
  useEffect(() => {
    if (!isOpen || staffAutoApplied) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/messaging/templates/default-exclusions");
        if (cancelled) return;
        const defaults = data?.lists || [];
        if (defaults.length) {
          setExcludeLists((prev) => {
            const seen = new Set(prev.map((l) => l.id));
            const merged = prev.slice();
            for (const l of defaults) if (!seen.has(l.id)) merged.push(l);
            return merged;
          });
        }
        setStaffAutoApplied(true);
      } catch { setStaffAutoApplied(true); }
    })();
    return () => { cancelled = true; };
  }, [isOpen, staffAutoApplied]);

  // Preview / send / batch
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [history, setHistory] = useState([]);
  const [batchClientId, setBatchClientId] = useState(() =>
    (window.crypto?.randomUUID?.() || `bc-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`));

  // MEDIA phase state · fonte di verità unica per l'header media del template
  // scelto. Aggiornato dal componente TemplateMediaInline via onRecordChange.
  // NESSUNA duplicazione: il record vive nella collection backend
  // `whatsapp_template_media`; qui c'è solo la view corrente in memoria.
  const [templateMediaRecord, setTemplateMediaRecord] = useState(null);
  const templateMediaReady = !!(templateMediaRecord?.exists && templateMediaRecord?.meta_media_id);
  const templateNeedsMedia = chosenTemplate && ["VIDEO", "IMAGE", "DOCUMENT"].includes(
    (chosenTemplate?.header_format || "").toUpperCase());

  // -----------------------------------------------------------------------
  // Load templates
  // -----------------------------------------------------------------------
  const loadTemplates = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoadingList(true);
    try {
      const url = "/admin/messaging/templates" + (refresh ? "?refresh=true" : "");
      const { data } = await api.get(url);
      setTemplates(data?.templates || []);
      setProviderVerified(!!data?.provider_verified);
      setLastRefreshedAt(data?.last_refreshed_at || null);
      if (refresh) setRefreshInfo(data?.refresh || null);
      if (data?.error) toast.error(`Meta: ${data.error}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingList(false); setRefreshing(false); }
  }, []);

  const runDiagnostic = useCallback(async () => {
    setDiagLoading(true); setDiagOpen(true);
    try {
      const { data } = await api.get("/admin/messaging/templates/diagnostic");
      setDiagData(data);
    } catch (e) { toast.error(formatApiError(e)); setDiagOpen(false); }
    finally { setDiagLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/messaging/templates/batches?limit=20");
      setHistory(data?.batches || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadTemplates(false);
      if (tab === "history") loadHistory();
    }
  }, [isOpen, tab, loadTemplates, loadHistory]);

  // -----------------------------------------------------------------------
  // Load recipients from CRM (server-side search + segment)
  // -----------------------------------------------------------------------
  const loadRecipients = useCallback(async () => {
    setRLoading(true);
    try {
      const params = new URLSearchParams();
      if (rSearch.trim()) params.set("q", rSearch.trim());
      if (rSegment) params.set("segment", rSegment);
      params.set("limit", "1000");
      const { data } = await api.get(`/admin/customers?${params.toString()}`);
      setRRows(Array.isArray(data) ? data : (data?.rows || []));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRLoading(false); }
  }, [rSearch, rSegment]);

  useEffect(() => {
    if (!isOpen || phase !== PHASE.RECIPIENTS) return;
    const t = setTimeout(loadRecipients, 250);   // debounce
    return () => clearTimeout(t);
  }, [isOpen, phase, loadRecipients]);

  // Derived: eligible/excluded rows (client-side hint; server preview validates).
  const derived = useMemo(() => {
    const eligible = []; const excluded = [];
    for (const r of rRows) {
      const raw = (r.phone || "").trim();
      if (!raw) { excluded.push({ ...r, _reason: "Telefono mancante" }); continue; }
      // Semplice euristica: >=8 cifre. Il server valida davvero in preview.
      const digits = raw.replace(/\D/g, "");
      if (digits.length < 8) { excluded.push({ ...r, _reason: "Telefono non valido" }); continue; }
      eligible.push(r);
    }
    return { eligible, excluded };
  }, [rRows]);

  // P0-6 · Il conteggio delle scelte dell'operatore è governato ESCLUSIVAMENTE
  // da `selectedIds` (Set globale, sopravvive a cambi di ricerca/filtro). NON
  // filtrarlo per `derived.eligible` (che contiene solo le rows correntemente
  // renderizzate): farlo mostrerebbe erroneamente "6 selezionati" quando
  // l'utente ne ha effettivamente scelti 13 in ricerche diverse.
  //
  // Semantica UI (spec §3 · SELEZIONATI ≠ DESTINATARI FINALI):
  //   chosenCount            = quanti l'operatore ha spuntato MANUALMENTE
  //                            (autoritativo qui, NON è l'audience)
  //   eligibleInViewCount    = quanti dei RENDERIZZATI passano la validazione
  //                            client-side (hint di anteprima locale)
  //   finalRecipientsCount   = quanti riceveranno REALMENTE il messaggio
  //                            (include liste, dedup e exclude server-side)
  //                            → è il valore che governa Contatore + Continua
  //   preview.totals.*       = verità server-side alla REVIEW (identica sorgente
  //                            del finalRecipientsCount authoritative)
  const chosenCount = selectAllFiltered
    ? derived.eligible.length
    : selectedIds.size;
  const eligibleInViewCount = selectAllFiltered
    ? derived.eligible.length
    : derived.eligible.filter((r) => selectedIds.has(r.id)).length;

  // P1-BUG-FIX · Sorgente unica per contatore/Continua/large-selection warning.
  // Popolata dal debounced /preview effect qui sotto.
  const [livePreview, setLivePreview] = useState(null);
  const [livePreviewLoading, setLivePreviewLoading] = useState(false);

  const finalRecipients = useMemo(() =>
    computeFinalRecipients({
      selectedIds, selectAllFiltered,
      filteredEligibleCount: derived.eligible.length,
      includeLists, livePreview,
    }),
    [selectedIds, selectAllFiltered, derived.eligible.length, includeLists, livePreview]);
  const finalRecipientsCount = finalRecipients.count;
  const hasAnyRecipientSource = finalRecipients.hasAnySource;
  const finalCountAuthoritative = finalRecipients.authoritative;

  // -----------------------------------------------------------------------
  // Selection payload builder · unica sorgente usata sia dal live-preview
  // debounced che dal Continua-click (`runPreview`). Riflette esattamente
  // la logica che il backend `preview_batch → _load_recipients` applica.
  // -----------------------------------------------------------------------
  const buildSelection = useCallback(() => {
    const hasLists = includeLists.length > 0 || excludeLists.length > 0;
    if (hasLists && includeLists.length > 0) {
      return { scope: "lists",
                filter: {},
                customer_ids: selectAllFiltered ? [] : Array.from(selectedIds),
                include_list_ids: includeLists.map((l) => l.id),
                exclude_list_ids: excludeLists.map((l) => l.id) };
    }
    if (selectAllFiltered) {
      return { scope: "filter",
                filter: { q: rSearch.trim(), segment: rSegment },
                customer_ids: [],
                include_list_ids: [],
                exclude_list_ids: excludeLists.map((l) => l.id) };
    }
    return { scope: "page",
              filter: {},
              customer_ids: Array.from(selectedIds),
              include_list_ids: [],
              exclude_list_ids: excludeLists.map((l) => l.id) };
  }, [includeLists, excludeLists, selectAllFiltered, selectedIds, rSearch, rSegment]);

  // -----------------------------------------------------------------------
  // Live preview count · debounced /preview call → conteggio autoritativo
  // (stesso motore di preview_batch, dedup + exclude + validazione
  // telefono già applicati). Attiva solo in RECIPIENTS con almeno una
  // sorgente di destinatari. Cancellabile via AbortController per evitare
  // race di risposte quando l'operatore digita/spunta velocemente.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen || phase !== PHASE.RECIPIENTS) return;
    if (!chosenTemplate) return;
    if (!hasAnyRecipientSource) {
      setLivePreview(null);
      return;
    }
    setLivePreviewLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const selection = buildSelection();
        const { data } = await api.post("/admin/messaging/templates/preview", {
          template_name: chosenTemplate.name,
          template_language: chosenTemplate.language,
          selection,
        }, { signal: controller.signal });
        setLivePreview(data);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        // Silenzioso: la stima client-side resta valida come fallback.
      } finally { setLivePreviewLoading(false); }
    }, 500);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [isOpen, phase, chosenTemplate, hasAnyRecipientSource, buildSelection]);

  // -----------------------------------------------------------------------
  // Preview → build server-side snapshot
  // -----------------------------------------------------------------------
  const runPreview = useCallback(async () => {
    if (!chosenTemplate) return;
    setPreviewLoading(true);
    try {
      const selection = buildSelection();
      // Ottimizzazione: se il live-preview è appena stato calcolato e non è
      // in loading, riusalo evitando una call inutile. Comportamento identico
      // sia per template BODY-only che per header media.
      let data;
      if (livePreview && !livePreviewLoading) {
        data = livePreview;
      } else {
        const resp = await api.post("/admin/messaging/templates/preview", {
          template_name: chosenTemplate.name,
          template_language: chosenTemplate.language,
          selection,
        });
        data = resp.data;
      }
      setPreview({ data, selection });
      // Se il template richiede header media (VIDEO/IMAGE/DOCUMENT), passa
      // dalla MEDIA phase PRIMA della Review, così l'operatore carica/vede
      // il media prima della conferma finale. Zero regressione per BODY-only:
      // vanno direttamente in REVIEW come sempre.
      const needsMedia = ["VIDEO", "IMAGE", "DOCUMENT"].includes(
        (chosenTemplate?.header_format || "").toUpperCase());
      setPhase(needsMedia ? PHASE.MEDIA : PHASE.REVIEW);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPreviewLoading(false); }
  }, [chosenTemplate, buildSelection, livePreview, livePreviewLoading]);

  // -----------------------------------------------------------------------
  // Send
  // -----------------------------------------------------------------------
  const runSend = useCallback(async () => {
    if (!preview || !chosenTemplate) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/messaging/templates/send", {
        batch_client_id: batchClientId,
        template_name: chosenTemplate.name,
        template_language: chosenTemplate.language,
        selection: preview.selection,
      });
      setCurrentBatch(data);
      setPhase(PHASE.PROGRESS);
      if (data.status === "blocked") {
        toast.error(`Batch bloccato: ${data.block_reason || "verifica Meta fallita"}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  }, [preview, chosenTemplate, batchClientId]);

  // P0-6 · Scheduling: same batch_client_id contract (idempotent), no worker
  // spawn. Freeze audience server-side, status=scheduled.
  const runSchedule = useCallback(async (scheduledForUtcISO, timezone, note, priority) => {
    if (!preview || !chosenTemplate) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/messaging/templates/schedule", {
        batch_client_id: batchClientId,
        template_name: chosenTemplate.name,
        template_language: chosenTemplate.language,
        selection: preview.selection,
        scheduled_for_utc: scheduledForUtcISO,
        scheduled_timezone: timezone || "Europe/Rome",
        note_operatore: note || null,
        priority: priority || "normal",
      });
      setCurrentBatch(data);
      setPhase(PHASE.PROGRESS);
      toast.success("Invio programmato");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  }, [preview, chosenTemplate, batchClientId]);

  useEffect(() => {
    if (phase !== PHASE.PROGRESS || !currentBatch?.batch_id) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/messaging/templates/batches/${currentBatch.batch_id}`);
        if (!alive) return;
        setCurrentBatch(data);
        if (!TERMINAL_BATCH_STATUSES.includes(data.status)) {
          setTimeout(tick, 2500);
        }
      } catch { if (alive) setTimeout(tick, 5000); }
    };
    setTimeout(tick, TERMINAL_BATCH_STATUSES.includes(currentBatch.status) ? 0 : 2000);
    return () => { alive = false; };
  }, [phase, currentBatch?.batch_id]);

  // -----------------------------------------------------------------------
  // Reset on close
  // -----------------------------------------------------------------------
  const handleClose = () => {
    setPhase(PHASE.PICKER); setPreview(null); setCurrentBatch(null);
    setConfirmSend(false); setChosenTemplate(null);
    setSelectedIds(new Set()); setSelectAllFiltered(false);
    setConfirmLargeSelection(false);
    setRSearch(""); setRSegment(""); setRRows([]);
    setTemplateMediaRecord(null);
    // P0-6 FASE 2G+ · Reset liste + reapply Staff default alla PROSSIMA apertura.
    setIncludeLists([]); setExcludeLists([]); setStaffAutoApplied(false);
    setListPickerMode(null);
    setBatchClientId(window.crypto?.randomUUID?.() || `bc-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4"
         data-testid="template-flow-root">
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-3 border-b border-sand-200">
          <div>
            <div className="eyebrow">WhatsApp</div>
            <h2 className="font-serif text-2xl mt-1">Template</h2>
          </div>
          <button onClick={handleClose} data-testid="template-flow-close"
                  className="p-1 opacity-60 hover:opacity-100"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs (only visible in PICKER or HISTORY) */}
        {(phase === PHASE.PICKER || phase === PHASE.PROGRESS) && (
          <div className="flex gap-1 border-b border-sand-300 px-5">
            <button onClick={() => setTab("send")}
                    data-testid="tab-send"
                    className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "send" ? "border-bronze-600 text-bronze-700 font-medium" : "border-transparent text-ink-500"}`}>
              Nuovo invio
            </button>
            <button onClick={() => { setTab("history"); loadHistory(); }}
                    data-testid="tab-history"
                    className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === "history" ? "border-bronze-600 text-bronze-700 font-medium" : "border-transparent text-ink-500"}`}>
              Invii recenti
            </button>
          </div>
        )}

        {/* Body — scrollable region */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === "history" && (phase === PHASE.PICKER) && (
            <HistoryTab history={history}
                         onSelect={(b) => { setCurrentBatch(b); setPhase(PHASE.PROGRESS); setTab("send"); }} />
          )}

          {tab === "send" && phase === PHASE.PICKER && (
            <PickerPhase
              templates={templates} loading={loadingList} refreshing={refreshing}
              providerVerified={providerVerified} lastRefreshedAt={lastRefreshedAt}
              chosen={chosenTemplate} onChoose={setChosenTemplate}
              onRefresh={() => loadTemplates(true)}
              onContinue={() => setPhase(PHASE.RECIPIENTS)}
              refreshInfo={refreshInfo}
              onDiagnostic={runDiagnostic}
              diagOpen={diagOpen} diagLoading={diagLoading} diagData={diagData}
              onCloseDiag={() => { setDiagOpen(false); setDiagData(null); }}
            />
          )}

          {phase === PHASE.RECIPIENTS && chosenTemplate && (
            <RecipientsPhase
              template={chosenTemplate}
              search={rSearch} setSearch={setRSearch}
              segment={rSegment} setSegment={setRSegment}
              rows={rRows} loading={rLoading}
              derived={derived}
              selectedIds={selectedIds} setSelectedIds={setSelectedIds}
              selectAllFiltered={selectAllFiltered}
              setSelectAllFiltered={setSelectAllFiltered}
              chosenCount={chosenCount}
              eligibleInViewCount={eligibleInViewCount}
              finalRecipientsCount={finalRecipientsCount}
              hasAnyRecipientSource={hasAnyRecipientSource}
              finalCountAuthoritative={finalCountAuthoritative}
              livePreviewLoading={livePreviewLoading}
              confirmLargeSelection={confirmLargeSelection}
              setConfirmLargeSelection={setConfirmLargeSelection}
              includeLists={includeLists} setIncludeLists={setIncludeLists}
              excludeLists={excludeLists} setExcludeLists={setExcludeLists}
              listPickerMode={listPickerMode} setListPickerMode={setListPickerMode}
            />
          )}

          {phase === PHASE.MEDIA && chosenTemplate && (
            <MediaPhase
              template={chosenTemplate}
              onBack={() => setPhase(PHASE.RECIPIENTS)}
              onContinue={() => setPhase(PHASE.REVIEW)}
              onRecordChange={setTemplateMediaRecord}
              mediaReady={templateMediaReady}
            />
          )}

          {phase === PHASE.REVIEW && (
            <ReviewPhase preview={preview} template={chosenTemplate}
                          onBack={() => setPhase(templateNeedsMedia ? PHASE.MEDIA : PHASE.RECIPIENTS)}
                          onConfirm={runSend} onSchedule={runSchedule} sending={sending}
                          confirmSend={confirmSend} setConfirmSend={setConfirmSend}
                          templateNeedsMedia={templateNeedsMedia}
                          templateMediaRecord={templateMediaRecord}
                          templateMediaReady={templateMediaReady}
                          onGoToMedia={() => setPhase(PHASE.MEDIA)} />
          )}

          {phase === PHASE.PROGRESS && (
            <ProgressPhase batch={currentBatch}
                            onClose={handleClose}
                            onViewHistory={() => { setPhase(PHASE.PICKER); setTab("history"); loadHistory(); }} />
          )}
        </div>

        {/* Sticky bottom nav for RECIPIENTS */}
        {phase === PHASE.RECIPIENTS && (
          <div className="border-t border-sand-200 p-3 sm:p-4 bg-white flex flex-wrap items-center justify-between gap-3">
            <Button variant="outline" onClick={() => setPhase(PHASE.PICKER)}
                    data-testid="btn-back-picker-recipients"
                    className="rounded-full">
              ← Torna ai template
            </Button>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-sm text-ink-700" data-testid="selection-count">
                <b data-testid="final-recipients-count">{finalRecipientsCount}</b> destinatari finali
                {livePreviewLoading && (
                  <span className="ml-1 text-[11px] text-ink-400"
                         data-testid="final-recipients-updating">
                    · aggiornamento…
                  </span>
                )}
                {!finalCountAuthoritative && !livePreviewLoading && hasAnyRecipientSource && (
                  <span className="ml-1 text-[11px] text-ink-500"
                         title="Stima client-side. La Review mostra il numero esatto (dedup + esclusioni server-side).">
                    · ~stima
                  </span>
                )}
              </div>
              <Button onClick={runPreview}
                      disabled={!hasAnyRecipientSource || finalRecipientsCount === 0 || previewLoading
                                 || (finalRecipientsCount > LARGE_SELECTION_THRESHOLD && !confirmLargeSelection)}
                      data-testid="btn-continue-recipients"
                      className="rounded-full bg-bronze-600 hover:bg-bronze-700 text-white">
                {previewLoading ? "Verifica…" : `Continua con ${finalRecipientsCount} destinatari →`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ===========================================================================
// PICKER
// ===========================================================================
function PickerPhase({ templates, loading, refreshing, providerVerified, lastRefreshedAt,
                       chosen, onChoose, onRefresh, onContinue,
                       refreshInfo, onDiagnostic, diagOpen, diagLoading, diagData, onCloseDiag }) {
  // Il gate sul media header è stato spostato nella PHASE.MEDIA dedicata:
  // qui il picker non deve più conoscere lo stato del media. L'operatore
  // sceglie il template e prosegue; se serve un media, lo carica nella
  // schermata "MEDIA DEL MESSAGGIO" prima della Review.
  const canContinue = providerVerified && chosen && chosen.usable;
  const zeroFromMeta = refreshInfo && refreshInfo.ok === true && (refreshInfo.count === 0);
  const refreshFailed = refreshInfo && refreshInfo.ok === false;
  return (
    <div data-testid="picker-phase">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-xs text-ink-500">
          {lastRefreshedAt ? `Ultimo refresh: ${new Date(lastRefreshedAt).toLocaleString("it-IT")}` : "Mai aggiornato"}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onDiagnostic} disabled={diagLoading}
                  data-testid="btn-diagnostic-templates" className="rounded-full">
            <Stethoscope className="w-3 h-3 mr-1" /> Diagnostica
          </Button>
          <Button size="sm" variant="outline" onClick={onRefresh} disabled={refreshing}
                  data-testid="btn-refresh-templates" className="rounded-full">
            <RefreshCcw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Aggiorna da Meta
          </Button>
        </div>
      </div>

      {refreshInfo && refreshInfo.ok && (
        <div data-testid="refresh-result" className="mb-3 rounded-2xl border-2 border-sand-300 bg-sand-50 p-3 text-xs text-ink-700">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><b>WABA:</b> <code>{refreshInfo.waba_id || "—"}</code></span>
            <span><b>Cred:</b> {refreshInfo.credential_source || "—"}</span>
            <span><b>Graph:</b> {refreshInfo.graph_version || "—"}</span>
            <span><b>Ricevuti:</b> {refreshInfo.count ?? 0}</span>
            <span className="text-emerald-800"><b>APPROVED:</b> {refreshInfo.approved_count ?? 0}</span>
          </div>
          {zeroFromMeta && (
            <div className="mt-2 rounded-lg bg-amber-100 border border-amber-300 p-2 text-amber-900">
              Meta ha risposto correttamente ma non ha restituito alcun template per questa WABA.
              Usa <b>Diagnostica</b> per dettagli.
            </div>
          )}
        </div>
      )}
      {refreshFailed && (
        <div data-testid="refresh-error" className="mb-3 rounded-2xl border-2 border-rose-300 bg-rose-50 p-3 text-xs text-rose-900">
          <div className="font-medium mb-1">Refresh fallito · {refreshInfo.reason}</div>
          <div className="whitespace-pre-wrap break-words">{refreshInfo.detail}</div>
        </div>
      )}

      {diagOpen && <DiagnosticPanel loading={diagLoading} data={diagData} onClose={onCloseDiag} />}

      {!providerVerified && (
        <div data-testid="provider-not-verified"
             className="mb-3 rounded-2xl border-2 border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Template non ancora verificato con WhatsApp in produzione. Clicca <b>Aggiorna da Meta</b> prima dell'invio.</span>
        </div>
      )}

      {loading && <div className="text-ink-400 text-sm">Caricamento templates…</div>}
      {!loading && templates.length === 0 && (
        <div className="text-ink-400 text-sm">Nessun template in cache. Clicca "Aggiorna da Meta".</div>
      )}

      <div className="space-y-2">
        {templates.map((t) => {
          const isChosen = chosen?.name === t.name && chosen?.language === t.language;
          const hasMediaHdr = ["VIDEO", "IMAGE", "DOCUMENT"].includes(
            (t.header_format || "").toUpperCase());
          return (
            <label key={`${t.name}-${t.language}`}
                    data-testid={`template-option-${t.name}`}
                    className={`block rounded-2xl border-2 p-3 cursor-pointer transition ${
                      !t.usable ? "border-sand-200 bg-sand-50 opacity-60 cursor-not-allowed" :
                      isChosen ? "border-bronze-500 bg-bronze-50" : "border-sand-300 hover:border-bronze-300"}`}>
              <div className="flex items-start gap-3">
                <input type="radio" name="tmpl" disabled={!t.usable}
                        checked={isChosen}
                        onChange={() => onChoose(t)} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{t.name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      t.status === "APPROVED" ? "bg-emerald-100 text-emerald-800" :
                      t.status === "PENDING" ? "bg-amber-100 text-amber-800" :
                      "bg-rose-100 text-rose-800"}`}>{t.status}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">{t.category}</span>
                    <span className="text-[10px] text-ink-500">{t.language}</span>
                    {hasMediaHdr && (
                      <span data-testid={`template-header-badge-${t.name}`}
                             className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 uppercase">
                        Header {t.header_format}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-600 mt-2 whitespace-pre-wrap line-clamp-3">{t.body_preview}</div>
                  {(t.buttons || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {t.buttons.map((b, i) => (
                        <span key={i} className="text-[10px] rounded-full border border-sand-300 px-2 py-0.5 bg-white">
                          {b.text}
                        </span>
                      ))}
                    </div>
                  )}
                  {isChosen && hasMediaHdr && (
                    <div data-testid={`template-media-hint-${t.name}`}
                          className="mt-3 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-[11px] text-violet-900 flex items-center gap-2">
                      <VideoIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>
                        Questo template richiede un <b>{t.header_format?.toLowerCase()}</b>.
                        Nel prossimo passaggio potrai caricarlo o vedere quello già associato.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </label>
          );
        })}
      </div>

      <div className="flex justify-end mt-4">
        <Button onClick={onContinue} disabled={!canContinue}
                data-testid="btn-goto-recipients"
                className="rounded-full bg-bronze-600 hover:bg-bronze-700 text-white">
          Scegli destinatari <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}


// ===========================================================================
// RECIPIENTS
// ===========================================================================
function RecipientsPhase({ template, search, setSearch, segment, setSegment,
                            rows, loading, derived,
                            selectedIds, setSelectedIds,
                            selectAllFiltered, setSelectAllFiltered,
                            chosenCount, eligibleInViewCount,
                            finalRecipientsCount, hasAnyRecipientSource,
                            finalCountAuthoritative, livePreviewLoading,
                            confirmLargeSelection, setConfirmLargeSelection,
                            includeLists, setIncludeLists,
                            excludeLists, setExcludeLists,
                            listPickerMode, setListPickerMode }) {
  const totalMatching = rows.length;
  const eligibleCount = derived.eligible.length;
  const excludedCount = derived.excluded.length;

  const toggleOne = (id) => {
    setSelectAllFiltered(false); setConfirmLargeSelection(false);
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };
  const deselectAll = () => {
    setSelectAllFiltered(false); setSelectedIds(new Set());
    setConfirmLargeSelection(false);
  };
  const toggleSelectAllFiltered = () => {
    if (selectAllFiltered) { deselectAll(); return; }
    setSelectAllFiltered(true); setSelectedIds(new Set());
    setConfirmLargeSelection(false);
  };

  return (
    <div data-testid="recipients-phase" className="space-y-4">
      {/* Header ridotto · MOSTRA i destinatari finali (autoritativo o stima)
          + separa chiaramente "spuntati manualmente" da "destinatari totali". */}
      <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-3">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="text-emerald-900 font-medium">{template.name}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-900">{template.status}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-200 text-sky-900">{template.category}</span>
          <span className="text-[10px] text-emerald-800">{template.language}</span>
          <span className="ml-auto text-xs text-emerald-800 flex items-center gap-1"
                 title="Destinatari finali = (liste incluse ∪ selezione manuale) − liste escluse − esclusioni server-side. Il numero esatto viene calcolato server-side e mostrato in Review.">
            <b data-testid="final-recipients-count-header">{finalRecipientsCount}</b> destinatari
            {livePreviewLoading && (
              <span className="text-emerald-700"
                     data-testid="final-recipients-updating-header">
                {" "}· aggiornamento…
              </span>
            )}
            {!finalCountAuthoritative && !livePreviewLoading && hasAnyRecipientSource && (
              <span className="text-emerald-700"
                     data-testid="final-recipients-estimate-header"
                     title="Stima client-side. Review mostra il numero esatto.">
                {" "}(~stima)
              </span>
            )}
            {chosenCount > 0 && (
              <span className="text-emerald-700"
                     data-testid="chosen-count-hint"
                     title="Contatti selezionati manualmente dalla tabella (non è l'audience: l'audience considera anche le liste).">
                {" "}· manuali: <b data-testid="chosen-count-value">{chosenCount}</b>
                {!selectAllFiltered && chosenCount !== eligibleInViewCount && (
                  <> (di cui {eligibleInViewCount} nella vista corrente)</>
                )}
              </span>
            )}
          </span>
        </div>
      </div>

      {/* P0-6 FASE 2G+ · INCLUDE / EXCLUDE LISTE (audience CRM server-side). */}
      <div className="grid md:grid-cols-2 gap-3">
        <div data-testid="include-lists-block"
              className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-emerald-800 flex items-center gap-1">
              <Plus className="w-3 h-3" /> Includi liste
            </div>
            <button onClick={() => setListPickerMode("include")}
                     data-testid="btn-open-include-picker"
                     className="text-xs text-emerald-700 hover:underline">+ Seleziona liste</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {includeLists.length === 0 && (
              <span className="text-xs text-ink-400">Nessuna lista inclusa</span>
            )}
            {includeLists.map((l) => (
              <span key={l.id} data-testid={`include-list-chip-${l.id}`}
                     className="inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 border font-medium"
                     style={{ borderColor: l.color, background: (l.color || "#94a3b8") + "1a", color: l.color }}>
                {l.name} ({l.members_count})
                <button onClick={() => setIncludeLists((s) => s.filter((x) => x.id !== l.id))}
                         data-testid={`include-list-remove-${l.id}`}>
                  <X className="w-3 h-3 opacity-60 hover:opacity-100" />
                </button>
              </span>
            ))}
          </div>
        </div>
        <div data-testid="exclude-lists-block"
              className="rounded-2xl border-2 border-rose-200 bg-rose-50/50 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-rose-800 flex items-center gap-1">
              <Minus className="w-3 h-3" /> Escludi liste <span className="text-[10px] font-normal opacity-70">(Staff default)</span>
            </div>
            <button onClick={() => setListPickerMode("exclude")}
                     data-testid="btn-open-exclude-picker"
                     className="text-xs text-rose-700 hover:underline">+ Seleziona liste</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {excludeLists.length === 0 && (
              <span className="text-xs text-ink-400">Nessuna lista esclusa</span>
            )}
            {excludeLists.map((l) => (
              <span key={l.id} data-testid={`exclude-list-chip-${l.id}`}
                     className="inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 border font-medium bg-white"
                     style={{ borderColor: l.color, color: l.color }}>
                {l.name} ({l.members_count})
                <button onClick={() => setExcludeLists((s) => s.filter((x) => x.id !== l.id))}
                         data-testid={`exclude-list-remove-${l.id}`}>
                  <X className="w-3 h-3 opacity-60 hover:opacity-100" />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      {listPickerMode && (
        <ListPickerModal
          title={listPickerMode === "include" ? "Includi liste" : "Escludi liste"}
          multiSelect
          preselected={new Set(
            (listPickerMode === "include" ? includeLists : excludeLists).map((l) => l.id))}
          onClose={() => setListPickerMode(null)}
          onMultiSelect={async (ids) => {
            // Ricarico i dettagli delle liste selezionate
            const details = [];
            for (const lid of ids) {
              try {
                const { data } = await (await import("@/lib/api")).api.get(`/admin/crm/lists/${lid}`);
                details.push(data);
              } catch { /* skip */ }
            }
            if (listPickerMode === "include") setIncludeLists(details);
            else setExcludeLists(details);
            setListPickerMode(null);
          }}
        />
      )}

      {/* Search + segment filters */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca nome, cognome, telefono, email…"
                  data-testid="recipients-search"
                  className="pl-9 h-11" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {SEGMENT_ORDER.map((s) => (
            <button key={s || "all"}
                     onClick={() => setSegment(s)}
                     data-testid={`recipients-segment-${s || "all"}`}
                     className={`text-xs px-3 py-1 rounded-full border transition ${
                       segment === s
                         ? "bg-bronze-600 text-white border-bronze-600"
                         : "bg-white text-ink-700 border-sand-300 hover:border-bronze-300"}`}>
              {SEGMENT_LABEL[s]}
            </button>
          ))}
          <div className="ml-auto text-xs text-ink-500">
            {loading ? "…" : (
              <>Trovati <b>{totalMatching}</b> · Inviabili <b>{eligibleCount}</b>
              {excludedCount > 0 && <> · Non inviabili <b>{excludedCount}</b></>}</>
            )}
          </div>
        </div>
      </div>

      {/* Select All / Deselect */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <button onClick={toggleSelectAllFiltered}
                 data-testid="btn-select-all-filtered"
                 disabled={eligibleCount === 0}
                 className={`px-3 py-1 rounded-full border transition ${
                   selectAllFiltered
                     ? "bg-emerald-600 text-white border-emerald-600"
                     : "bg-white text-emerald-800 border-emerald-400 hover:bg-emerald-50"}
                   disabled:opacity-40`}>
          {selectAllFiltered
            ? `✓ Tutti i ${eligibleCount} risultati filtrati selezionati`
            : `Seleziona tutti i ${eligibleCount} risultati filtrati`}
        </button>
        {(selectedIds.size > 0 || selectAllFiltered) && (
          <button onClick={deselectAll} data-testid="btn-deselect-all"
                  className="px-3 py-1 rounded-full border border-sand-300 text-ink-600 hover:border-rose-300">
            Deseleziona tutti
          </button>
        )}
      </div>

      {/* Conferma large-selection · basata sui DESTINATARI FINALI, non sui
          soli spunti manuali (una lista da 5000 senza manuali deve triggerare). */}
      {finalRecipientsCount > LARGE_SELECTION_THRESHOLD && !confirmLargeSelection && (
        <div data-testid="large-selection-warning"
              className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium mb-1">
              Selezione grande ({finalRecipientsCount} destinatari finali)
            </div>
            <div>Verifica prima di continuare. Ogni invio ha un costo Meta.</div>
          </div>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={confirmLargeSelection}
                    onChange={(e) => setConfirmLargeSelection(e.target.checked)}
                    data-testid="chk-confirm-large-selection" />
            <span>Ho verificato</span>
          </label>
        </div>
      )}

      {/* Rows */}
      <div className="space-y-2" data-testid="recipients-list">
        {loading && <div className="text-ink-400 text-sm">Caricamento…</div>}
        {!loading && totalMatching === 0 && (
          <div className="text-ink-400 text-sm">Nessun cliente corrisponde a ricerca/filtro correnti.</div>
        )}

        {derived.eligible.map((r) => {
          const isChecked = selectAllFiltered || selectedIds.has(r.id);
          return (
            <label key={r.id} data-testid={`recipient-row-${r.id}`}
                    className={`flex items-center gap-3 rounded-xl border p-2.5 cursor-pointer transition ${
                      isChecked ? "border-emerald-400 bg-emerald-50" : "border-sand-200 hover:border-emerald-300"}`}>
              <input type="checkbox" checked={isChecked}
                      onChange={() => toggleOne(r.id)}
                      disabled={selectAllFiltered}
                      data-testid={`recipient-check-${r.id}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <span className="font-medium">{r.name || "—"}</span>
                  {r.surname && <span> {r.surname}</span>}
                  {r.segment && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-sand-100 text-ink-600 uppercase">
                      {SEGMENT_LABEL[r.segment] || r.segment}
                    </span>
                  )}
                </div>
                <div className="text-xs text-ink-500 flex items-center gap-3 flex-wrap">
                  {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                </div>
              </div>
            </label>
          );
        })}

        {derived.excluded.map((r) => (
          <div key={r.id} data-testid={`excluded-row-${r.id}`}
                className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-2.5 opacity-70">
            <input type="checkbox" disabled className="opacity-50" />
            <div className="flex-1 min-w-0">
              <div className="text-sm">
                <span className="font-medium">{r.name || "—"}</span>
                {r.surname && <span> {r.surname}</span>}
              </div>
              <div className="text-xs text-rose-800">{r._reason}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ===========================================================================
// REVIEW
// ===========================================================================
function ReviewPhase({ preview, template, onBack, onConfirm, onSchedule, sending, confirmSend, setConfirmSend,
                        templateNeedsMedia = false, templateMediaRecord = null, templateMediaReady = false, onGoToMedia }) {
  const [mode, setMode] = React.useState("now");   // "now" | "schedule"
  const [schedDate, setSchedDate] = React.useState("");
  const [schedTime, setSchedTime] = React.useState("");
  const [schedNote, setSchedNote] = React.useState("");
  const [schedPriority, setSchedPriority] = React.useState("normal");
  const TZ = "Europe/Rome";
  if (!preview) return null;
  const d = preview.data;
  const eligible = d?.totals?.eligible ?? 0;
  return (
    <div data-testid="review-phase">
      <div className="text-sm mb-3">
        <b>Template:</b> {template?.name} · {template?.language} · {template?.category}
      </div>

      {/* Media allegato · visibile solo per template con HEADER media */}
      {templateNeedsMedia && (
        <div data-testid="review-media-summary"
              className={`rounded-2xl border-2 p-3 mb-3 text-sm flex items-start gap-3 ${
                templateMediaReady ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}>
          {(() => {
            const fmt = (template?.header_format || "").toUpperCase();
            const Icon = fmt === "VIDEO" ? VideoIcon : fmt === "IMAGE" ? ImageIcon : FileText;
            return <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${templateMediaReady ? "text-emerald-700" : "text-rose-700"}`} />;
          })()}
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] uppercase tracking-wider ${templateMediaReady ? "text-emerald-700" : "text-rose-700"}`}>
              Media allegato · Header {template?.header_format}
            </div>
            {templateMediaReady && templateMediaRecord ? (
              <div className="mt-0.5">
                <b className="truncate block" data-testid="review-media-filename">
                  {templateMediaRecord.original_filename}
                </b>
                <div className="text-[11px] text-ink-600 mt-0.5 flex flex-wrap gap-x-3">
                  <span>{(templateMediaRecord.byte_size / 1024 / 1024).toFixed(2)} MB</span>
                  <span>{templateMediaRecord.mime_type}</span>
                  <span className="text-emerald-800 font-semibold">✓ Pronto</span>
                </div>
              </div>
            ) : (
              <div className="mt-1">
                <b className="text-rose-900">Media mancante</b>
                <div className="text-[11px] text-rose-800 mt-0.5">
                  Questo template richiede un {(template?.header_format || "").toLowerCase()}.
                  Carica il media prima di procedere.
                </div>
                <button type="button" onClick={onGoToMedia}
                         data-testid="btn-goto-media-from-review"
                         className="mt-2 inline-flex items-center gap-1 rounded-full bg-bronze-600 hover:bg-bronze-700 text-white px-3 py-1 text-xs">
                  ← Torna al media
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="rounded-2xl border-2 border-sand-300 p-4 mb-3 bg-sand-50">
        <div className="text-xs eyebrow mb-2">Riepilogo destinatari</div>
        <div data-testid="review-totals" className="text-sm space-y-1">
          <div><b data-testid="review-selected">{d?.totals?.selected ?? 0}</b> selezionati dall'operatore</div>
          <div className="text-emerald-800">✓ <b data-testid="review-eligible">{eligible}</b> destinatari finali</div>
          <div className="text-rose-800">⚠ <b data-testid="review-excluded">{d?.totals?.excluded ?? 0}</b> esclusi</div>
        </div>
        {/* Motivazioni aggregate quando esistono esclusi (spec §3) */}
        {(d?.excluded || []).length > 0 && (() => {
          const byReason = {};
          for (const e of (d.excluded || [])) {
            const k = e.reason || "unknown";
            byReason[k] = (byReason[k] || 0) + 1;
          }
          const REASON_LABEL = {
            excluded_by_list: "Escluso da lista (es. Staff)",
            no_first_name: "Manca il nome (variabile {{1}})",
            no_phone: "Numero mancante",
            invalid_phone: "Numero non valido",
            marketing_refused: "Consenso marketing rifiutato",
            marketing_not_expressed: "Consenso marketing non espresso",
            unknown: "Altra regola",
          };
          return (
            <div className="mt-3 pt-3 border-t border-sand-200"
                  data-testid="review-reasons-breakdown">
              <div className="text-[10px] uppercase text-ink-500 tracking-wider mb-1">Motivi esclusione</div>
              <div className="space-y-0.5 text-xs text-rose-900">
                {Object.entries(byReason).map(([k, n]) => (
                  <div key={k} data-testid={`review-reason-${k}`}
                        className="flex justify-between">
                    <span>{REASON_LABEL[k] || k}</span>
                    <b>{n}</b>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {d?.sample_render && (
        <div className="rounded-2xl border-2 border-sand-300 p-4 mb-3 bg-white">
          <div className="text-xs eyebrow mb-2">Anteprima (destinatario esempio: {d.sample_render.first_name})</div>
          <div className="whitespace-pre-wrap text-sm text-ink-700">{d.sample_render.body_rendered}</div>
          {(d?.template_buttons || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2 pt-3 border-t border-sand-200">
              {d.template_buttons.map((b, i) => (
                <span key={i} className="text-xs rounded-full border border-sand-300 px-3 py-1 bg-sand-50">{b.text}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {(d?.excluded || []).length > 0 && (
        <details className="mb-3">
          <summary className="text-xs cursor-pointer text-ink-500">Vedi {d.excluded.length} esclusi</summary>
          <div className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1">
            {d.excluded.map((x, i) => (
              <div key={i} data-testid={`review-excluded-${i}`}
                    className="rounded-lg bg-rose-50 border border-rose-200 px-2 py-1 flex justify-between gap-2">
                <span>{x.name || x.customer_id}</span>
                <code className="text-rose-800">{x.reason}</code>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 mb-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Non è possibile annullare l'invio dopo la conferma finale.</span>
      </div>

      <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer">
        <input type="checkbox" checked={confirmSend} onChange={(e) => setConfirmSend(e.target.checked)}
                data-testid="chk-confirm-send" />
        <span>Confermo l'invio di <b>{eligible}</b> messaggi WhatsApp
          {" "}(destinatari finali, dopo esclusioni server-side).</span>
      </label>

      {/* P0-6 · Toggle Invia ora / Programma */}
      <div className="rounded-2xl border-2 border-sand-300 p-3 mb-3 bg-white" data-testid="scheduling-block">
        <div className="flex gap-2 mb-3">
          <button onClick={() => setMode("now")}
                   data-testid="btn-mode-now"
                   className={`flex-1 rounded-full px-4 py-2 text-sm border-2 ${mode === "now" ? "bg-bronze-600 text-white border-bronze-600" : "bg-white border-sand-300"}`}>
            Invia ora
          </button>
          <button onClick={() => setMode("schedule")}
                   data-testid="btn-mode-schedule"
                   className={`flex-1 rounded-full px-4 py-2 text-sm border-2 ${mode === "schedule" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white border-sand-300"}`}>
            Programma invio
          </button>
        </div>
        {mode === "schedule" && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] eyebrow">Data</label>
                <input type="date" value={schedDate}
                        onChange={(e) => setSchedDate(e.target.value)}
                        data-testid="input-sched-date"
                        min={new Date(Date.now() + 2*60*1000).toISOString().slice(0, 10)}
                        className="w-full h-11 px-3 rounded-lg border border-sand-300" />
              </div>
              <div>
                <label className="text-[10px] eyebrow">Ora</label>
                <input type="time" value={schedTime}
                        onChange={(e) => setSchedTime(e.target.value)}
                        data-testid="input-sched-time"
                        className="w-full h-11 px-3 rounded-lg border border-sand-300" />
              </div>
            </div>
            <div className="text-xs text-ink-500">Fuso orario: <b>{TZ}</b></div>
            <div data-testid="sched-granularity-notice"
                 className="rounded-lg bg-amber-50 border border-amber-300 p-2 text-[11px] text-amber-900 flex items-start gap-1.5">
              <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>L'invio verrà <b>avviato indicativamente entro 15 minuti</b> dall'orario programmato
                (lo scheduler controlla ogni 15 minuti). Il consenso marketing viene rivalutato al momento dell'invio.</span>
            </div>
            <div>
              <label className="text-[10px] eyebrow">Priorità</label>
              <select value={schedPriority} onChange={(e) => setSchedPriority(e.target.value)}
                       data-testid="select-sched-priority"
                       className="w-full h-11 px-3 rounded-lg border border-sand-300">
                <option value="low">Bassa</option>
                <option value="normal">Normale</option>
                <option value="high">Alta</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] eyebrow">Note operatore (opzionale)</label>
              <textarea value={schedNote} onChange={(e) => setSchedNote(e.target.value.slice(0, 500))}
                         data-testid="input-sched-note" rows={2}
                         placeholder="Es. Campagna fine stagione, coordinato con Filippo…"
                         className="w-full px-3 py-2 rounded-lg border border-sand-300 resize-none" />
              <div className="text-[10px] text-ink-400 text-right">{schedNote.length}/500</div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack} data-testid="btn-back-recipients" className="rounded-full">
          ← Torna ai destinatari
        </Button>
        <Button onClick={() => {
                  if (mode === "now") { onConfirm(); return; }
                  // Compose local datetime in Europe/Rome, then convert to UTC ISO.
                  if (!schedDate || !schedTime) { toast.error("Data e ora obbligatorie"); return; }
                  const localISO = `${schedDate}T${schedTime}:00`;
                  const local = new Date(localISO);   // interpretato in local browser TZ
                  const nowPlus2 = new Date(Date.now() + 2*60*1000);
                  if (local <= nowPlus2) { toast.error("La data/ora deve essere almeno +2 minuti"); return; }
                  onSchedule?.(local.toISOString(), "Europe/Rome", schedNote, schedPriority);
                }}
                 disabled={!confirmSend || sending || eligible === 0
                            || (mode === "schedule" && (!schedDate || !schedTime))
                            || (templateNeedsMedia && !templateMediaReady)}
                 data-testid={mode === "now" ? "btn-confirm-send" : "btn-confirm-schedule"}
                 className={`rounded-full text-white ${mode === "now" ? "bg-bronze-600 hover:bg-bronze-700" : "bg-emerald-700 hover:bg-emerald-800"}`}>
          <Send className="w-4 h-4 mr-1" />
          {sending ? "…" : (mode === "now" ? `Invia a ${eligible} clienti` : `Programma invio a ${eligible} clienti`)}
        </Button>
      </div>
    </div>
  );
}


// ===========================================================================
// PROGRESS + HISTORY + DIAGNOSTIC (invariati)
// ===========================================================================


// ===========================================================================
// MEDIA · nuova phase dedicata al media header del template.
// Riusa integralmente TemplateMediaInline (variant="full") + le stesse
// route backend (`/api/admin/messaging/templates/{name}/media[...]`) e
// la stessa collection `whatsapp_template_media`. Nessuna duplicazione.
// Compare SOLO quando chosenTemplate.header_format ∈ VIDEO/IMAGE/DOCUMENT.
// ===========================================================================
function MediaPhase({ template, onBack, onContinue, onRecordChange, mediaReady }) {
  const fmt = (template?.header_format || "").toUpperCase();
  const FormatIcon = fmt === "VIDEO" ? VideoIcon
                   : fmt === "IMAGE" ? ImageIcon
                   : FileText;
  const label = fmt === "VIDEO" ? "video"
              : fmt === "IMAGE" ? "immagine"
              : "documento";
  return (
    <div data-testid="media-phase">
      <div className="mb-4">
        <div className="eyebrow flex items-center gap-2">
          <FormatIcon className="w-4 h-4 text-violet-700" />
          Media del messaggio
        </div>
        <h3 className="font-serif text-xl mt-1">MEDIA DEL MESSAGGIO</h3>
        <p className="text-sm text-ink-600 mt-2">
          Il template <b>{template?.name}</b> ha un HEADER di tipo{" "}
          <b className="uppercase">{fmt}</b>. Carica il {label} da usare come
          intestazione del messaggio. Se ne hai già associato uno, lo vedi qui
          sotto — puoi sostituirlo, rimuoverlo o rinnovarlo.
        </p>
      </div>

      <TemplateMediaInline template={template}
                            variant="full"
                            onRecordChange={onRecordChange} />

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={onBack}
                 data-testid="btn-back-media"
                 className="rounded-full">
          ← Torna ai destinatari
        </Button>
        <Button onClick={onContinue} disabled={!mediaReady}
                 data-testid="btn-continue-media"
                 className="rounded-full bg-bronze-600 hover:bg-bronze-700 text-white disabled:opacity-50">
          Continua alla review <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {!mediaReady && (
        <div data-testid="media-required-warning"
              className="mt-3 rounded-xl bg-rose-50 border border-rose-300 p-3 text-xs text-rose-900">
          <b>Questo template richiede un {label}.</b> Carica il media prima di procedere.
        </div>
      )}
    </div>
  );
}


// ===========================================================================
// PROGRESS + HISTORY + DIAGNOSTIC (invariati)
// ===========================================================================
const TERMINAL_BATCH_STATUSES = ["completed", "partially_failed", "failed", "blocked", "cancelled"];

const OUTCOME_BANNER = {
  completed:        { cls: "bg-emerald-50 border-emerald-300 text-emerald-900", Icon: CheckCircle2, text: "Invio completato, nessun fallimento." },
  partially_failed: { cls: "bg-orange-50 border-orange-300 text-orange-900", Icon: MessageCircleWarning, text: "Invio concluso con alcuni messaggi falliti." },
  failed:           { cls: "bg-rose-50 border-rose-300 text-rose-900", Icon: MessageCircleWarning, text: "Invio fallito: nessun messaggio consegnato." },
};

function ProgressPhase({ batch, onClose, onViewHistory }) {
  if (!batch) return null;
  const t = batch.totals || {};
  const isBlocked = batch.status === "blocked";
  const outcome = OUTCOME_BANNER[batch.status];
  const isCompleted = !!outcome;
  const awaiting = t.current_states?.sent ?? 0;
  return (
    <div data-testid="progress-phase">
      {isBlocked && (
        <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-4 mb-3 text-sm text-rose-900" data-testid="batch-blocked">
          <div className="font-medium mb-1 flex items-center gap-2"><MessageCircleWarning className="w-4 h-4" /> Batch bloccato</div>
          <div className="text-xs">{batch.block_reason || "verifica pre-invio fallita"}</div>
        </div>
      )}
      <div className="text-sm mb-3">
        <b>Template:</b> {batch.template_name}
        <span className="text-ink-500"> · {batch.template_category}</span>
      </div>
      <div className="rounded-2xl border-2 border-sand-300 p-4 space-y-1 text-sm">
        <ProgressRow label="Selezionati" value={t.selected} />
        <ProgressRow label="Inviabili"   value={t.eligible} />
        <ProgressRow label="Inviati"     value={t.sent}      color="text-emerald-800" />
        <ProgressRow label="Consegnati"  value={t.delivered} color="text-emerald-700" />
        <ProgressRow label="Letti"       value={t.read}      color="text-sky-800" />
        <ProgressRow label="Falliti"     value={t.failed}    color="text-rose-800" />
        <ProgressRow label="Esito incerto" value={t.unknown} color="text-amber-800" />
        {t.skipped > 0 && <ProgressRow label="Saltati (consenso)" value={t.skipped} color="text-amber-800" />}
        {t.pending > 0 && <ProgressRow label="In coda" value={t.pending} color="text-ink-500" />}
        {awaiting > 0 && <ProgressRow label="In attesa di conferma consegna" value={awaiting} color="text-ink-500" />}
      </div>
      {!isCompleted && !isBlocked && (
        <div className="text-xs text-ink-500 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Aggiornamento automatico ogni 2s</div>
      )}
      {isCompleted && (
        <div className={`rounded-2xl border-2 p-3 mt-3 text-sm flex items-center gap-2 ${outcome.cls}`}
             data-testid={batch.status === "completed" ? "batch-completed" : `batch-${batch.status.replace("_", "-")}`}>
          <outcome.Icon className="w-4 h-4" /> {outcome.text}
        </div>
      )}
      <BatchFailuresList failures={batch.failures} />
      <div className="flex justify-between mt-4">
        <Button variant="outline" onClick={onViewHistory} data-testid="btn-view-history" className="rounded-full">Vedi invii recenti</Button>
        <Button onClick={onClose} data-testid="btn-close-progress" className="rounded-full bg-bronze-600 hover:bg-bronze-700 text-white">Chiudi</Button>
      </div>
    </div>
  );
}

export function BatchFailuresList({ failures }) {
  if (!failures || failures.length === 0) return null;
  return (
    <div className="mt-3 rounded-2xl border-2 border-rose-200 bg-rose-50/60 p-3" data-testid="batch-failures">
      <div className="text-xs font-medium text-rose-900 mb-2">Messaggi non riusciti ({failures.length})</div>
      <div className="space-y-2 max-h-56 overflow-auto">
        {failures.map((f) => (
          <div key={f.item_id} className="text-xs rounded-lg bg-white border border-rose-200 p-2"
               data-testid={`batch-failure-${f.item_id}`}>
            <div className="flex justify-between gap-2">
              <span className="font-medium text-ink-800">{f.display_name || "—"} <span className="font-mono text-ink-500">{f.wa_id}</span></span>
              <span className={f.state === "failed" ? "text-rose-700" : "text-amber-700"}>
                {f.state === "failed" ? "Fallito" : "Esito incerto"}
              </span>
            </div>
            <div className="mt-1 text-ink-700" data-testid={`batch-failure-code-${f.item_id}`}>
              {f.error_code ? <>Codice <b>{f.error_code}</b>{f.error_description ? ` · ${f.error_description}` : ""}</> : "Codice non disponibile"}
            </div>
            {f.error_detail && <div className="text-ink-500 mt-0.5">{f.error_detail}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressRow({ label, value, color }) {
  return (
    <div className="flex justify-between">
      <span className="text-ink-500">{label}</span>
      <span className={`tabular-nums font-medium ${color || ""}`}>{value ?? 0}</span>
    </div>
  );
}


const HISTORY_STATUS_LABEL = {
  completed: "Completato", partially_failed: "Parziale", failed: "Fallito",
  in_progress: "In esecuzione", processing: "In esecuzione", scheduled: "Programmato",
  cancelled: "Annullato", blocked: "Bloccato",
};

function HistoryTab({ history, onSelect }) {
  if (!history || history.length === 0) {
    return <div className="text-ink-400 text-sm">Nessun invio recente.</div>;
  }
  return (
    <div className="space-y-2" data-testid="history-list">
      {history.map((b) => (
        <button key={b.batch_id} onClick={() => onSelect(b)}
                data-testid={`history-row-${b.batch_id}`}
                className="w-full text-left rounded-2xl border-2 border-sand-300 hover:border-bronze-300 p-3 transition">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-medium text-sm">{b.template_name}</div>
              <div className="text-xs text-ink-500">{new Date(b.created_at).toLocaleString("it-IT")}</div>
            </div>
            <div className="text-xs">
              <span className={`px-2 py-0.5 rounded-full ${
                b.status === "completed" ? "bg-emerald-100 text-emerald-800" :
                b.status === "partially_failed" ? "bg-orange-100 text-orange-800" :
                b.status === "failed" ? "bg-rose-100 text-rose-800" :
                b.status === "in_progress" ? "bg-amber-100 text-amber-800" :
                b.status === "blocked" ? "bg-rose-100 text-rose-800" : "bg-sand-100"}`}>
                {HISTORY_STATUS_LABEL[b.status] || b.status}
              </span>
              <span className="ml-2 text-ink-500 tabular-nums">
                {b.totals?.sent ?? 0}/{b.totals?.eligible ?? 0}
              </span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}


function DiagnosticPanel({ loading, data, onClose }) {
  return (
    <div data-testid="diagnostic-panel"
         className="mb-3 rounded-2xl border-2 border-sky-300 bg-sky-50 p-3 text-xs">
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium text-sky-900">Diagnostica catena refresh</div>
        <button onClick={onClose} className="opacity-60 hover:opacity-100" data-testid="btn-close-diag">
          <X className="w-3 h-3" />
        </button>
      </div>
      {loading && <div className="text-ink-500">Verifica in corso…</div>}
      {!loading && data && (
        <div className="space-y-2 text-ink-800">
          <Row label="config_present" value={String(data.config_present)} />
          <Row label="waba_id" value={data.waba_id || "—"} mono />
          <Row label="phone_number_id" value={data.phone_number_id || "—"} mono />
          <Row label="graph_version" value={data.graph_version || "—"} />
          <Row label="provider_verified" value={String(data.template_provider_verified)} />
          <div className="mt-2 pt-2 border-t border-sky-200">
            <div className="font-medium mb-1">Credential resolver</div>
            <Row label="ok" value={String(data.credential_resolver?.ok)} />
            <Row label="source" value={data.credential_resolver?.source || data.credential_resolver?.error || "—"} />
          </div>
          <div className="mt-2 pt-2 border-t border-sky-200">
            <div className="font-medium mb-1">Chiamata Meta</div>
            <Row label="attempted" value={String(data.meta_call?.attempted)} />
            <Row label="ok" value={String(data.meta_call?.ok)} />
            <Row label="url_path" value={data.meta_call?.url_path || "—"} mono />
            <Row label="http_statuses" value={(data.meta_call?.http_statuses || []).join(", ") || "—"} />
            <Row label="items_count" value={String(data.meta_call?.items_count ?? "—")} />
            {data.meta_call?.error && (
              <div className="rounded-lg bg-rose-100 border border-rose-300 p-2 mt-1 text-rose-900 break-words">
                <b>error:</b> {data.meta_call.error}
              </div>
            )}
            {data.meta_call?.meta_error && (
              <div className="rounded-lg bg-rose-100 border border-rose-300 p-2 mt-1 text-rose-900">
                <b>meta_error:</b>
                <div className="mt-1 space-y-0.5">
                  <Row label="code" value={String(data.meta_call.meta_error.code ?? "—")} />
                  <Row label="subcode" value={String(data.meta_call.meta_error.subcode ?? "—")} />
                  <Row label="type" value={data.meta_call.meta_error.type || "—"} />
                  <Row label="message" value={data.meta_call.meta_error.message || "—"} />
                  <Row label="fbtrace_id" value={data.meta_call.meta_error.fbtrace_id || "—"} mono />
                </div>
              </div>
            )}
          </div>
          <div className="mt-2 pt-2 border-t border-sky-200">
            <div className="font-medium mb-1">Cache Mongo</div>
            <Row label="count" value={String(data.cache?.count ?? 0)} />
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-ink-500 w-40 flex-shrink-0">{label}</span>
      <span className={`${mono ? "font-mono" : ""} break-all`}>{value}</span>
    </div>
  );
}
